import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import {Link, useLocation} from 'react-router-dom';

interface SidebarImportMenuProps {
    isCollapsed: boolean;
}

const SidebarImportMenu: React.FC<SidebarImportMenuProps> = ({isCollapsed}) => {
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number } | null>(null);

    const menuItems = [
        // { id: 'main', title: 'Page Principale', icon: 'bi-house', path: '/import' },
        {id: 'gestion', title: 'Gestion des Imports', icon: 'bi-box-seam', path: '/import/gestion'},
        {id: 'livraisons', title: 'Gestion des Livraisons', icon: 'bi-truck', path: '/import/livraisons'},
        {id: 'livraison-recap', title: 'Récap Livraison', icon: 'bi-clipboard', path: '/import/livraison-recap'},
        {
            id: 'recuperations-simple',
            title: 'Récupérations',
            icon: 'bi-arrow-counterclockwise',
            path: '/recuperations-simple'
        },
        {
            id: 'recuperations',
            title: 'Gestion des Récupérations',
            icon: 'bi-arrow-repeat',
            path: '/import/recuperations'
        },
        {id: 'detention', title: 'Détention', icon: 'bi-hourglass-split', path: '/import/detention'},
        {id: 'recap', title: 'Récap Import', icon: 'bi-table', path: '/recap/import'}

    ];


    const isActive = (path: string) => location.pathname === path;
    const isAnyImportActive = () => menuItems.some(item => isActive(item.path));

    if (isCollapsed) {
        return (
            <div
                className="relative"
                onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setFlyoutPos({top: rect.top, left: rect.right + 8});
                    setIsExpanded(true);
                }}
                onMouseLeave={() => setIsExpanded(false)}
            >
                {isAnyImportActive() && (
                    <span
                        className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)] z-10"/>
                )}
                <div
                    className={`group py-3 mx-3 my-0.5 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        isAnyImportActive()
                            ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <i className={`bi bi-truck text-[17px] transition-transform duration-200 ${isAnyImportActive() ? '' : 'group-hover:scale-110'}`}></i>
                </div>

                {/* Sous-menu en "flyout" : la sidebar réduite (64px) ne peut pas accueillir
                    un sous-menu déplié verticalement — on l'affiche donc à droite de l'icône,
                    par-dessus le contenu, au survol/clic (pattern standard des sidebars compactes). */}
                {isExpanded && flyoutPos && createPortal(
                    <div
                        style={{position: 'fixed', top: flyoutPos.top, left: flyoutPos.left}}
                        onMouseEnter={() => setIsExpanded(true)}
                        onMouseLeave={() => setIsExpanded(false)}
                        className="w-60 rounded-xl bg-green-900/95 backdrop-blur-sm ring-1 ring-white/10 shadow-2xl shadow-black/40 py-2 z-[1100] animate-[fadeIn_0.15s_ease-out]">
                        <p className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-300/70">Imports</p>

                        {/*{quickActions.map((item) => (*/}
                        {/*    <Link*/}
                        {/*        key={item.id}*/}
                        {/*        to={item.path}*/}
                        {/*        className={`flex items-center py-2 px-4 mx-2 rounded-lg no-underline text-[12.5px] font-semibold transition-all duration-200 ${*/}
                        {/*            isActive(item.path)*/}
                        {/*                ? 'bg-emerald-500/90 text-white shadow-sm'*/}
                        {/*                : 'text-emerald-300 hover:bg-emerald-500/15 hover:text-emerald-200'*/}
                        {/*        }`}*/}
                        {/*    >*/}
                        {/*        <i className={`bi ${item.icon} mr-2 text-sm`}></i>*/}
                        {/*        <span className="flex-1">{item.title}</span>*/}
                        {/*    </Link>*/}
                        {/*))}*/}

                        <div
                            className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-1.5 mx-4"/>

                        {menuItems.map((item) => (
                            <Link
                                key={item.id}
                                to={item.path}
                                className={`flex items-center py-2 px-4 mx-2 rounded-lg no-underline text-[12.5px] transition-all duration-200 ${
                                    isActive(item.path)
                                        ? 'bg-white/15 text-white font-medium ring-1 ring-white/10'
                                        : 'text-white/65 hover:bg-white/10 hover:text-white/90'
                                }`}
                            >
                                <i className={`bi ${item.icon} mr-2 text-sm`}></i>
                                <span className="flex-1">{item.title}</span>
                            </Link>
                        ))}
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    return (
        <div className="mb-1.5 relative">
            {/* Indicateur actif */}
            {isAnyImportActive() && (
                <span
                    className="absolute left-1.5 top-4 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)] z-10"/>
            )}

            {/* Bouton principal Import */}
            <div
                className={`group py-2.5 px-4 mx-3 my-0.5 rounded-xl cursor-pointer flex items-center justify-between transition-all duration-200 ${
                    isAnyImportActive()
                        ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center">
                    <i className={`bi bi-truck text-[17px] mr-3 transition-transform duration-200 ${isAnyImportActive() ? '' : 'group-hover:scale-110'}`}></i>
                    <span className="text-[13px] font-medium tracking-wide">Imports</span>
                </div>
                <i
                    className={`bi bi-chevron-down text-xs transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                />
            </div>

            {/* Sous-menu */}
            <div
                className={`overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="mx-3 my-1 rounded-xl bg-black/15 ring-1 ring-white/5 backdrop-blur-sm py-2 space-y-0.5">
                    {/* Actions rapides */}
                    {/*{quickActions.map((item) => (*/}
                    {/*    <Link*/}
                    {/*        key={item.id}*/}
                    {/*        to={item.path}*/}
                    {/*        className={`flex items-center py-2 px-3 mx-2 rounded-lg no-underline text-[12.5px] font-semibold transition-all duration-200 ${*/}
                    {/*            isActive(item.path)*/}
                    {/*                ? 'bg-emerald-500/90 text-white shadow-sm'*/}
                    {/*                : 'text-emerald-300 hover:bg-emerald-500/15 hover:text-emerald-200'*/}
                    {/*        }`}*/}
                    {/*    >*/}
                    {/*        <i className={`bi ${item.icon} mr-2 text-sm`}></i>*/}
                    {/*        <span className="flex-1">{item.title}</span>*/}
                    {/*    </Link>*/}
                    {/*))}*/}

                    {/* Séparateur */}
                    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent my-1.5 mx-3"/>

                    {/* Menu standard */}
                    {menuItems.map((item) => (
                        <Link
                            key={item.id}
                            to={item.path}
                            className={`flex items-center py-2 px-3 mx-2 rounded-lg no-underline text-[12.5px] transition-all duration-200 ${
                                isActive(item.path)
                                    ? 'bg-white/15 text-white font-medium ring-1 ring-white/10'
                                    : 'text-white/65 hover:bg-white/10 hover:text-white/90'
                            }`}
                        >
                            <i className={`bi ${item.icon} mr-2 text-sm`}></i>
                            <span className="flex-1">{item.title}</span>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SidebarImportMenu;
